import { Injectable, inject } from '@angular/core';
import { ApiV1_Product } from '@pedix-workspace/api-v1';
import {
  computeHeadlineDiscount,
  extractHeadlineVariants,
} from '@pedix-workspace/shared-promotions';
import { AppRequestContextService } from './app-request-context.service';
import { CartPromotionsService } from './cart-promotions.service';

@Injectable({ providedIn: 'root' })
export class PromotionsService {
  private appRequestContext = inject(AppRequestContextService);
  private cartPromotionsService = inject(CartPromotionsService);

  hasPromotionsInCategory(categoryId: string): boolean {
    const category = this.appRequestContext.allCategories().find(c => c.id === categoryId);
    if (!category?.products) {
      return false;
    }
    const promoMap = this.cartPromotionsService.availablePromotionsByProductId();
    return category.products.some(product => product.active && promoMap.has(product.id));
  }

  /**
   * Single source of truth for the "HASTA X% OFF" headline of a product on the catalog.
   * Combines price-individual + bundle + quantity promos via `floor(max)`. Never stacks.
   */
  getHeadlineDiscountForProduct(product: ApiV1_Product): number {
    const variants = extractHeadlineVariants({
      price: product.price,
      priceDiscount: product.priceDiscount,
      presentationItems: product.presentations?.items,
    });
    const promos =
      this.cartPromotionsService.availablePromotionsByProductId().get(product.id) ?? [];
    return computeHeadlineDiscount(variants, promos);
  }

  /**
   * Max headline discount across all active products of a category.
   */
  getMaxHeadlineDiscountForCategory(categoryId: string): number {
    const category = this.appRequestContext.allCategories().find(c => c.id === categoryId);
    if (!category?.products) {
      return 0;
    }
    let max = 0;
    for (const product of category.products) {
      if (!product.active) {
        continue;
      }
      const headline = this.getHeadlineDiscountForProduct(product);
      if (headline > max) {
        max = headline;
      }
    }
    return max;
  }
}
